// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — pages/Compras/Fornecedores.tsx
//
//  Tela piloto do PKG-00 (contrato de consulta): busca, situação, período de
//  cadastro, ordenação e paginação no SERVIDOR; KPIs sobre o filtro inteiro;
//  filtros na URL (voltar da edição mantém a consulta). USA-COM-05/06.
// ════════════════════════════════════════════════════════════════════════════

import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Building2, Eye, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useFornecedoresConsulta } from '@/features/compras/hooks/useFornecedor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BarraFiltros } from '@/components/BarraFiltros';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { SortableTableHead } from '@/components/SortableTableHead';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { alternarOrdenacao, type DirecaoOrdenacao } from '@/lib/consultas';

type Campo = 'codigo' | 'nome' | 'cpfCnpj' | 'email' | 'cadastro' | 'situacao';
type Situacao = 'ativos' | 'inativos' | 'todos';

const PADROES = {
  busca: '',
  situacao: 'ativos',
  dtInicio: '',
  dtFim: '',
  ordenarPor: 'nome',
  direcao: 'asc',
  pagina: 1,
  tamanhoPagina: 20,
};

const Fornecedores = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);

  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });

  const { lista, resumo } = useFornecedoresConsulta({
    busca:         filtros.busca || undefined,
    situacao:      filtros.situacao as Situacao,
    dtInicio:      filtros.dtInicio || undefined,
    dtFim:         filtros.dtFim || undefined,
    ordenarPor:    filtros.ordenarPor,
    direcao:       filtros.direcao as DirecaoOrdenacao,
    pagina:        pag.pagina,
    tamanhoPagina: pag.tamanhoPagina,
  });

  const fornecedores = lista.data?.itens ?? [];
  const ordenacao = { ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao };
  const onOrdenar = (campo: Campo) =>
    setFiltros(alternarOrdenacao(ordenacao, campo, campo === 'cadastro' ? 'desc' : 'asc'));

  // Volta para a mesma consulta (filtros na URL) ao sair do detalhe.
  const abrir = (id: number) => navigate(`/compras/fornecedores/${id}`, { state: { voltarPara: location.search } });

  const kpi = (valor?: number) => (resumo.isLoading ? '…' : valor ?? 0);
  const cabecalho = { ordenarPor: filtros.ordenarPor, direcao: ordenacao.direcao, onOrdenar };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Fornecedores</h1>
          <p className="text-muted-foreground mt-1">Gerencie os fornecedores cadastrados</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/compras/fornecedores/novo')}>
          <Plus className="h-4 w-4" />Novo Fornecedor
        </Button>
      </div>

      {/* KPIs — calculados no servidor sobre a busca e o período (todas as páginas) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-500" />Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpi(resumo.data?.total)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {filtrosAtivos > 0 ? 'Fornecedores no filtro' : 'Fornecedores cadastrados'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{kpi(resumo.data?.ativos)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Inativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{kpi(resumo.data?.inativos)}</div>
          </CardContent>
        </Card>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Buscar por código, nome, CPF/CNPJ, e-mail ou telefone..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={limparFiltros}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Situação</Label>
          <Select value={filtros.situacao} onValueChange={(situacao) => setFiltros({ situacao })}>
            <SelectTrigger className="h-9 w-[130px]" aria-label="Situação"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ativos">Ativos</SelectItem>
              <SelectItem value="inativos">Inativos</SelectItem>
              <SelectItem value="todos">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FiltroPeriodo
          rotulo="Cadastro"
          inicio={filtros.dtInicio}
          fim={filtros.dtFim}
          onChange={({ inicio, fim }) => setFiltros({ dtInicio: inicio, dtFim: fim })}
        />
        {lista.isFetching && !lista.isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead campo="codigo" {...cabecalho} className="w-24">Código</SortableTableHead>
                <SortableTableHead campo="nome" {...cabecalho}>Nome / Razão Social</SortableTableHead>
                <SortableTableHead campo="cpfCnpj" {...cabecalho}>CPF/CNPJ</SortableTableHead>
                <SortableTableHead campo="email" {...cabecalho}>E-mail</SortableTableHead>
                <TableHead>Telefone</TableHead>
                <SortableTableHead campo="cadastro" {...cabecalho}>Cadastro</SortableTableHead>
                <SortableTableHead campo="situacao" {...cabecalho}>Status</SortableTableHead>
                <TableHead className="w-16">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lista.isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : lista.isError ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-destructive">
                    Não foi possível carregar os fornecedores.
                  </TableCell>
                </TableRow>
              ) : fornecedores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Nenhum fornecedor encontrado
                    {filtrosAtivos > 0 && (
                      <Button variant="link" className="ml-1 h-auto p-0" onClick={limparFiltros}>
                        — limpar filtros
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                fornecedores.map(f => (
                  <TableRow key={f.id} className="cursor-pointer" onDoubleClick={() => abrir(f.id)}>
                    <TableCell className="tabular-nums text-muted-foreground">{f.id}</TableCell>
                    <TableCell className="font-medium">
                      {f.nomeRazao}
                      {f.apelido && <span className="text-muted-foreground text-xs ml-1">({f.apelido})</span>}
                    </TableCell>
                    <TableCell>{f.cpfCnpj ?? '—'}</TableCell>
                    <TableCell>{f.email ?? '—'}</TableCell>
                    <TableCell>{f.fone ?? f.celular ?? '—'}</TableCell>
                    <TableCell className="tabular-nums">
                      {f.dtCriacao ? format(new Date(f.dtCriacao), 'dd/MM/yyyy') : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge className={f.isAtivo
                        ? 'bg-green-500/10 text-green-600'
                        : 'bg-red-500/10 text-red-600'}>
                        {f.isAtivo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Visualizar"
                        onClick={() => abrir(f.id)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape(lista.data)} />
    </div>
  );
};

export default Fornecedores;
